import { FC } from 'react';
import { Input, ModalProps, Select, Space } from 'antd';
import ModalAtom from 'components/atoms/ModalAtom';
import DefaultButton from 'components/atoms/Button/DefaultButton';
import PrimaryButton from 'components/atoms/Button/PrimaryButton';
import TableAtom from 'components/atoms/TableAtom';
import LinkButton from 'components/atoms/Button/LinkButton';
import {
  AssetCustomData,
  AssignToAssetModalAssetsOptions,
  AssignToAssetModalClientsOptions,
  AssignToAssetModalOptions,
  AssignToAssetModalProjectsOptions,
} from 'components/pages/GatewaysListPage/resources/utils';
import './styles.scss';

export interface AssignGatewayToAssetModalTranslations {
  title: string;
  mainButtonText: string;
  secondaryButtonText: string;
  searchText: string;
  searchPlaceholderText: string;
  errorSearchingMessage: string;
  errorAssetNotSelectedMessage: string;
  manualSearchButtonText: string;
  clientPlaceholderText: string;
  projectPlaceholderText: string;
  assetPlaceholderText: string;

}

interface Props extends ModalProps {
  clientsOptions: AssignToAssetModalClientsOptions;
  projectsOptions: AssignToAssetModalProjectsOptions;
  assetsOptions: AssignToAssetModalAssetsOptions;
  modalOptions: AssignToAssetModalOptions;
  assigning: boolean;
}

const TABLE_HEIGHT = 300;

const AssignGatewayToAssetModal: FC<Props> = ({
  clientsOptions,
  projectsOptions,
  assetsOptions,
  modalOptions,
  onCancel,
  assigning,
  ...props
}) => {
  const { clients, selectedClient, onSelectClient, loadingClients } = clientsOptions;
  const { projects, selectedProject, onSelectSearchedProject, loadingProjects } = projectsOptions;
  const {
    assets,
    selectedAsset,
    assignGatewayToAssetModalColumns,
    onSelectAssetToBeLinked,
    onValidateSearchAssets,
    loadingAssets,
    loadingManualAssets,
  } = assetsOptions;

  const {
    assignGatewayToAssetModalTranslations,
    onAssignGatewayToAsset,
    onClickAssignGatewayToAssetModaSearch,
    onClickAssignGatewayToAssetModalManualSearch,
    errorSearching,
    errorAssigning,
    searchingVisible,
    manualSearchingVisible,
    searchTableVisible,
  } = modalOptions;

  const {
    title,
    mainButtonText,
    secondaryButtonText,
    errorSearchingMessage,
    errorAssetNotSelectedMessage,
    searchPlaceholderText,
    searchText,
    manualSearchButtonText,
    clientPlaceholderText,
    projectPlaceholderText,
    assetPlaceholderText,
  } = assignGatewayToAssetModalTranslations;

  return (
    <ModalAtom
      {...props}
      title={<Space>{title}</Space>}
      onCancel={onCancel}
      destroyOnClose
      width={'50%'}
      footer={[
        <DefaultButton key={'back'} onClick={onCancel} title={secondaryButtonText} />,
        <PrimaryButton key={'submit'} loading={assigning} onClick={onAssignGatewayToAsset} title={mainButtonText} />,
      ]}
    >
      <div className={'AssignGatewayToAssetModal__wrapper'}>
        <div className={manualSearchingVisible ? 'AssignGatewayToAssetModal__buttons-gap' : ''}>
          <LinkButton
            className={'AssignGatewayToAssetModal__manual-searching-button'}
            title={searchText}
            onClick={onClickAssignGatewayToAssetModaSearch}
          />
          {searchingVisible && (
            <>
              <Input.Search
                className={'AssignGatewayToAssetModal__search-button'}
                allowClear
                placeholder={searchPlaceholderText}
                onSearch={onValidateSearchAssets}
                loading={loadingAssets}
                status={errorSearching ? 'error' : undefined}

              />
              <div className={'AssignGatewayToAssetModal__error-message-wrapper'}>
                {errorSearching && <p className={'AssignGatewayToAssetModal__error-message'}>{errorSearchingMessage}</p>}
              </div>
              {searchTableVisible && (
                <TableAtom<AssetCustomData>
                  className={'AssignGatewayToAssetModal__table'}
                  loading={loadingAssets}
                  bordered={true}
                  columns={assignGatewayToAssetModalColumns}
                  rowKey={'value'}
                  dataSource={assets}
                  pagination={false}
                  size={'small'}
                  scroll={{ y: TABLE_HEIGHT }}
                  rowSelection={{
                    type: 'radio',
                    hideSelectAll: true,
                    onChange: (selectedRowKeys: React.Key[], selectedRows: AssetCustomData[]) => onSelectAssetToBeLinked(selectedRows[0].value),
                  }}
                />
              )}
            </>
          )}
        </div>
        <LinkButton
          className={'AssignGatewayToAssetModal__manual-searching-button'}
          title={manualSearchButtonText}
          onClick={onClickAssignGatewayToAssetModalManualSearch}
        />
        {manualSearchingVisible && (
          <>
            <div className={'AssignGatewayToAssetModal__manual-selectors'}>
              <Select
                className={'AssignGatewayToAssetModal__selector'}
                value={selectedClient}
                showSearch
                placeholder={clientPlaceholderText}
                onChange={onSelectClient}
                options={clients}
                loading={loadingClients}
                filterOption={(input: string, option?: { label: string; value: string }) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
              <Select
                className={'AssignGatewayToAssetModal__selector'}
                value={selectedProject}
                showSearch
                placeholder={projectPlaceholderText}
                onChange={onSelectSearchedProject}
                options={projects}
                loading={loadingProjects}
                filterOption={(input: string, option?: { label: string; value: string }) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </div>
            <div className={'AssignGatewayToAssetModal__manual-selectors'}>
              <Select
                className={'AssignGatewayToAssetModal__selector'}
                value={selectedAsset}
                showSearch
                placeholder={assetPlaceholderText}
                onChange={onSelectAssetToBeLinked}
                options={assets}
                loading={loadingManualAssets}
                filterOption={(input: string, option?: { label: string; value: string }) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </div>
          </>
        )}
      </div>
      <div className={'AssignGatewayToAssetModal__error-message-wrapper'}>
        {errorAssigning && <p className={'AssignGatewayToAssetModal__error-message'}>{errorAssetNotSelectedMessage}</p>}
      </div>
    </ModalAtom>
  );
}

export default AssignGatewayToAssetModal;